/**
 * Click and hover handlers for interactive (SVG layer) site plan overlays.
 *
 * Consolidates the click handler (previously interactiveSitePlanClick.js)
 * and hover handlers into a single module.
 *
 * Injected into interactiveSitePlan.plugin via the onSitePlanClick,
 * onSitePlanEnter, and onSitePlanLeave options so the plugin stays free of
 * store, DOM event, and navigation dependencies.
 *
 * Click behaviour:
 *   Desktop – dispatches 'interactiveMapClick' analytics event and navigates.
 *   Mobile  – first tap dispatches 'sitePlanTapped' custom event (opens the
 *             carousel); second tap on the same community navigates.
 *
 * Hover behaviour:
 *   Applies/removes the active CSS class on the community-level wrapper element
 *   and dispatches 'sitePlanHovered' / 'sitePlanHoverEnded' custom events so
 *   the sitePlanMiniCard.plugin can react without coupling to this handler.
 *
 * @module searchResultsMap/handlers/interactiveSitePlanHandlers
 */

import MarkerType from '../enums/markerType';
import { selectSitePlansById, selectMapResults } from '../redux/selectors';
import { sitePlanTapped$, sitePlanHovered$, sitePlanHoverEnded$ } from '../redux/observables';
import { SITE_PLAN_CLASS_NAMES } from '../services/sitePlanBehavior';

/**
 * Creates the interactive site plan click handler.
 *
 * @param {Function} getState    - Returns the current Redux store state.
 * @param {Function} isDesktopFn - Returns true when the viewport is desktop.
 * @returns {Function} Handler called with { sitePlanId } on overlay click.
 */
export function createInteractiveSitePlanClickHandler(getState, isDesktopFn) {
  let lastTappedCommunityId = null;

  return function handleInteractiveSitePlanClick({ sitePlanId }) {
    const sitePlan = selectSitePlansById(getState())[String(sitePlanId)];
    if (!sitePlan) {
      return;
    }

    const results = selectMapResults(getState()) ?? [];
    const preferredTypes = [MarkerType.HousingDevelopment.toString(), MarkerType.BuildablePlan.toString()];
    const preferredMatch = results.find(({ communityId: resultCommunityId, type }) =>
      resultCommunityId === sitePlan.communityId && preferredTypes.includes(type)
    );
    const resultMatch = preferredMatch ?? results.find(({ communityId: resultCommunityId }) =>
      resultCommunityId === sitePlan.communityId
    );
    const isPreferredMatch = Boolean(preferredMatch);
    
    const url = preferredMatch?.url ?? `/communitydetail/community-${resultMatch?.communityId}`;

    document.body.dispatchEvent(
      new CustomEvent('interactiveMapClick', {
        bubbles: true,
        detail: {
          communityMap: {
            id: resultMatch?.communityId,
            name: resultMatch?.communityName ?? resultMatch?.name,
            isInteractive: true,
            builderId: resultMatch?.builderId,
            builderName: resultMatch?.builderName,
          },
        },
      })
    );

    if (isDesktopFn?.() || !isPreferredMatch) {
      window.open(url, '_blank');
      return;
    }

    // Mobile: second tap on the same community navigates.
    if (lastTappedCommunityId === sitePlan.communityId) {
      window.open(url, '_blank');
      lastTappedCommunityId = null;
      return;
    }

    lastTappedCommunityId = sitePlan.communityId;
    sitePlanTapped$.next({ communityId: sitePlan.communityId });
  };
}

/**
 * Creates hover handlers for interactive site plan overlays.
 * Targets the community-level wrapper (data-site-plan-community-id)
 * written by svgOverlays.js so hovering any plan in a community highlights the
 * entire community wrapper.
 *
 * Dispatches 'sitePlanHovered' and 'sitePlanHoverEnded' custom events so
 * sitePlanMiniCard.plugin can react without coupling to this handler.
 *
 * @returns {{ onSitePlanEnter: Function, onSitePlanLeave: Function }}
 */
export function createInteractiveSitePlanHoverHandler() {
  function getCommunityWrapper(communityId) {
    return document.querySelector(`[data-site-plan-community-id="${communityId}"]`);
  }

  return {
    onSitePlanEnter(communityId) {
      getCommunityWrapper(communityId)?.classList.add(SITE_PLAN_CLASS_NAMES.active);
      sitePlanHovered$.next({ communityId });
    },
    onSitePlanLeave(communityId) {
      getCommunityWrapper(communityId)?.classList.remove(SITE_PLAN_CLASS_NAMES.active);
      sitePlanHoverEnded$.next({ communityId });
    },
  };
}

export default createInteractiveSitePlanHoverHandler;